import { Link } from 'react-router-dom';
import type { Page } from '@connaro/shared';
import { api } from '../api/client';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Deployment {
  id: string;
  status: string;
  paused: boolean;
  createdAt: string;
}

const inProgress = (s: string): boolean =>
  ['PENDING', 'IN_PROGRESS', 'PLANNING', 'STAGED', 'VERIFYING'].includes(s);

export function DeploymentsPage(): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<Page<Deployment>>('/deployments?limit=50'),
    [],
  );

  const action = (id: string, verb: string) => () =>
    void api.post(`/deployments/${id}/${verb}`).then(reload);

  return (
    <section>
      <div className="page-head">
        <h1>{t('deployment.title')}</h1>
      </div>
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error.message} onRetry={reload} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Created</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link to={`/deployments/${d.id}`}>
                      <code>{d.id.slice(0, 8)}</code>
                    </Link>
                  </td>
                  <td>
                    <span className={`chip chip--${d.status.toLowerCase()}`}>{d.status}</span>
                    {d.paused ? <span className="chip chip--warning">paused</span> : null}
                  </td>
                  <td>{new Date(d.createdAt).toLocaleString()}</td>
                  <td className="row-actions">
                    {inProgress(d.status) ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={action(d.id, d.paused ? 'resume' : 'pause')}
                        >
                          {d.paused ? t('deployment.resume') : t('deployment.pause')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--danger"
                          onClick={action(d.id, 'cancel')}
                        >
                          {t('deployment.cancel')}
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
